import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Fade,
  FormControl,
  FormLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

const FORM_SECTIONS = [
  {
    key: "identity",
    name: "IDENTITY",
    questions: [
      {
        id: "identify_parties",
        text: "Fully identify themselves and other parties?",
      },
      {
        id: "state_name",
        text: "State their name?",
      },
      {
        id: "state_role_company",
        text: "State their role (and company where appropriate)?",
      },
      {
        id: "state_location",
        text: "State the location from which they are speaking?",
      },
      {
        id: "prompt_other_party",
        text: "Prompt the other party if they did not identify who they were and where they were speaking from?",
      },
      {
        id: "confirm_correct_person",
        text: "Repeat back to confirm they were speaking to the correct person and that they understood where the other person was?",
      },
    ],
  },
  {
    key: "message_delivery",
    name: "MESSAGE CONTENT (DELIVERY)",
    questions: [
      {
        id: "state_intention",
        text: "Make it clear what the intention or purpose of the communication is?",
      },
      {
        id: "logical_structure",
        text: "Deliver the message in a logical structure?",
      },
      {
        id: "concise_delivery",
        text: "Deliver the message concisely without any superfluous chat?",
      },
      {
        id: "accurate_information",
        text: "Deliver the information accurately?",
      },
      {
        id: "all_relevant_information",
        text: "Deliver all the relevant information?",
      },
      {
        id: "phonetic_alphabet",
        text: "Use the phonetic alphabet appropriately?",
      },
      {
        id: "numbers_singularly",
        text: "Use numbers singularly as appropriate?",
      },
      {
        id: "standard_terms",
        text: "Use standard job terms or phrases?",
      },
      {
        id: "twenty_four_hour_clock",
        text: "Convey times in the 24-hour clock format?",
      },
      {
        id: "avoid_slang",
        text: "Avoid using slang or local terminology?",
      },
    ],
  },
  {
    key: "message_receiving",
    name: "MESSAGE CONTENT (RECEIVING)",
    questions: [
      {
        id: "listen_information",
        text: "Listen to the information being provided?",
      },
      {
        id: "clarify_questions",
        text: "Ask questions to clarify understanding?",
      },
      {
        id: "confirm_relevant_information",
        text: "Ask questions to confirm they have all the relevant information?",
      },
      {
        id: "acknowledgements",
        text: "Use acknowledgements (e.g., “uh-huh”) to indicate they are listening?",
      },
      {
        id: "persist_questioning",
        text: "Persist with questioning until confident all relevant information has been received?",
      },
    ],
  },
  {
    key: "confirming_understanding",
    name: "CONFIRMING UNDERSTANDING",
    questions: [
      {
        id: "repeat_back",
        text: "Repeat back or summarise the critical details?",
      },
      {
        id: "prompt_other_party_repeat",
        text: "Prompt or challenge the other party if they do not repeat back?",
      },
      {
        id: "persist_until_clear",
        text: "Persist until comfortable that a clear understanding has been reached?",
      },
      {
        id: "identify_correct_errors",
        text: "Identify and correct errors or inconsistencies in the repeat back?",
      },
    ],
  },
  {
    key: "next_steps",
    name: "NEXT STEPS",
    questions: [
      {
        id: "confirm_contact_details",
        text: "Confirm that contact details are exchanged?",
      },
      {
        id: "agree_next_steps",
        text: "Only conclude the communication when the next step has been agreed?",
      },
    ],
  },
  {
    key: "overall",
    name: "OVERALL",
    questions: [
      {
        id: "confident_tone",
        text: "Use a confident tone of voice throughout?",
      },
      {
        id: "speak_slowly",
        text: "Speak slowly and clearly throughout?",
      },
      {
        id: "avoid_interrupting",
        text: "Avoid interrupting or speaking over the other party?",
      },
      {
        id: "professional_polite",
        text: "Remain professional and polite throughout the exchange?",
      },
      {
        id: "remain_assertive",
        text: "Remain assertive even if the other party is challenging?",
      },
      {
        id: "accurate_message",
        text: "Convey or receive an accurate message?",
      },
    ],
  },
];

const ANSWER_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "na", label: "N/A" },
];

const OVERALL_RATING_OPTIONS = [
  { value: "competent", label: "Competent" },
  { value: "competent_with_development", label: "Competent with Development" },
  { value: "developing", label: "Developing" },
  { value: "not_yet_competent", label: "Not Yet Competent" },
];

const QUESTION_TEXT_MAP = FORM_SECTIONS.reduce((map, section) => {
  section.questions.forEach((question) => {
    map[question.text] = { sectionKey: section.key, questionId: question.id };
  });
  return map;
}, {});

const buildInitialAnswers = () => {
  const defaults = {};

  FORM_SECTIONS.forEach((section) => {
    defaults[section.key] = {};
    section.questions.forEach((question) => {
      defaults[section.key][question.id] = { answer: "", comment: "" };
    });
  });

  return defaults;
};

const buildPayload = ({
  playlistId,
  recordedCallId,
  answers,
  overallRating,
  developmentPlan,
}) => {
  const sections = FORM_SECTIONS.map((section) => {
    const responses = section.questions
      .map((question) => {
        const response = answers?.[section.key]?.[question.id];

        if (!response?.answer) {
          return null;
        }

        const payload = {
          question: question.text,
          answer: response.answer,
        };

        if (response.comment?.trim()) {
          payload.comment = response.comment.trim();
        }

        return payload;
      })
      .filter(Boolean);

    return {
      name: section.name,
      responses,
    };
  }).filter((section) => section.responses.length > 0);

  const allAnswered = FORM_SECTIONS.every((section) =>
    section.questions.every((question) => {
      const answer = answers?.[section.key]?.[question.id]?.answer;
      return Boolean(answer);
    })
  );

  const trimmedDevelopmentPlan = {
    nature_of_need: developmentPlan.nature_of_need.trim(),
    plan: developmentPlan.plan.trim(),
    target_completion_date: developmentPlan.target_completion_date,
  };

  const hasDevelopmentPlan =
    trimmedDevelopmentPlan.nature_of_need ||
    trimmedDevelopmentPlan.plan ||
    trimmedDevelopmentPlan.target_completion_date;

  return {
    playlist_id: playlistId,
    recorded_call_id: recordedCallId,
    sections,
    overall_rating: overallRating || null,
    development_plan: hasDevelopmentPlan ? trimmedDevelopmentPlan : null,
    completed: allAnswered,
  };
};

const NRSpokenCommsReviewForm = ({
  playlistId,
  recordedCallId,
  onPayloadChange,
  initialPayload,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState(buildInitialAnswers);
  const [overallRating, setOverallRating] = useState("");
  const [developmentPlan, setDevelopmentPlan] = useState({
    nature_of_need: "",
    plan: "",
    target_completion_date: "",
  });
  const lastLoadedKeyRef = useRef(null);

  const payload = useMemo(
    () =>
      buildPayload({
        playlistId,
        recordedCallId,
        answers,
        overallRating,
        developmentPlan,
      }),
    [answers, overallRating, developmentPlan, playlistId, recordedCallId]
  );

  useEffect(() => {
    if (onPayloadChange) {
      onPayloadChange(payload);
    }
  }, [payload, onPayloadChange]);

  useEffect(() => {
    if (!initialPayload) return;

    const payloadKey = `${
      initialPayload.playlist_id ?? playlistId
    }:${initialPayload.recorded_call_id ?? recordedCallId}`;

    if (lastLoadedKeyRef.current === payloadKey) {
      return;
    }

    const nextAnswers = buildInitialAnswers();
    initialPayload.sections?.forEach((section) => {
      section.responses?.forEach((response) => {
        const mapped = QUESTION_TEXT_MAP[response.question];
        if (!mapped) return;
        const { sectionKey, questionId } = mapped;
        nextAnswers[sectionKey][questionId] = {
          answer: response.answer || "",
          comment: response.comment || "",
        };
      });
    });

    setAnswers(nextAnswers);
    setOverallRating(initialPayload.overall_rating || "");
    setDevelopmentPlan({
      nature_of_need: initialPayload.development_plan?.nature_of_need || "",
      plan: initialPayload.development_plan?.plan || "",
      target_completion_date:
        initialPayload.development_plan?.target_completion_date || "",
    });
    setActiveStep(0);
    lastLoadedKeyRef.current = payloadKey;
  }, [initialPayload, playlistId, recordedCallId]);

  const handleAnswerChange = useCallback((sectionKey, questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [questionId]: {
          ...prev[sectionKey][questionId],
          answer,
        },
      },
    }));
  }, []);

  const handleCommentChange = useCallback((sectionKey, questionId, comment) => {
    setAnswers((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [questionId]: {
          ...prev[sectionKey][questionId],
          comment,
        },
      },
    }));
  }, []);

  const handleDevelopmentPlanChange = useCallback((field, value) => {
    setDevelopmentPlan((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleNext = () => {
    setActiveStep((prev) =>
      Math.min(prev + 1, FORM_SECTIONS.length - 1)
    );
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleStepSelect = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const getSectionProgress = useCallback(
    (sectionKey) => {
      const section = FORM_SECTIONS.find((s) => s.key === sectionKey);
      if (!section) return 0;

      const answered = section.questions.filter(
        (q) => answers?.[sectionKey]?.[q.id]?.answer
      ).length;

      return section.questions.length
        ? (answered / section.questions.length) * 100
        : 0;
    },
    [answers]
  );

  const totalQuestions = useMemo(
    () =>
      FORM_SECTIONS.reduce(
        (sum, section) => sum + section.questions.length,
        0
      ),
    []
  );

  const totalAnswered = useMemo(
    () =>
      FORM_SECTIONS.reduce((sum, section) => {
        const answered = section.questions.filter(
          (q) => answers?.[section.key]?.[q.id]?.answer
        ).length;
        return sum + answered;
      }, 0),
    [answers]
  );

  const overallProgress = totalQuestions
    ? (totalAnswered / totalQuestions) * 100
    : 0;

  const currentSection = FORM_SECTIONS[activeStep];
  const currentSectionProgress = getSectionProgress(currentSection.key);
  const answeredInSection = currentSection.questions.filter(
    (q) => answers?.[currentSection.key]?.[q.id]?.answer
  ).length;

  const getAnswerVisuals = useCallback(
    (answer) => {
      switch (answer) {
        case "yes":
          return {
            label: "Yes",
            background: theme.palette.success.light,
            color: theme.palette.success.main,
          };
        case "no":
          return {
            label: "No",
            background: theme.palette.error.light,
            color: theme.palette.error.main,
          };
        case "na":
          return {
            label: "N/A",
            background: theme.palette.warning.light,
            color: theme.palette.warning.main,
          };
        default:
          return {
            label: "Awaiting response",
            background: theme.palette.action.hover,
            color: theme.palette.text.secondary,
          };
      }
    },
    [theme.palette.action.hover, theme.palette.error, theme.palette.success, theme.palette.text.secondary, theme.palette.warning]
  );

  const getOptionPalette = useCallback(
    (value) => {
      if (value === "yes") {
        return {
          base: theme.palette.success.main,
          contrast: theme.palette.success.contrastText,
        };
      }
      if (value === "no") {
        return {
          base: theme.palette.error.main,
          contrast: theme.palette.error.contrastText,
        };
      }
      return {
        base: theme.palette.warning.main,
        contrast: theme.palette.warning.contrastText,
      };
    },
    [theme.palette.error, theme.palette.success, theme.palette.warning]
  );

  return (
    <Stack spacing={3}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Typography variant="h6" fontWeight={600}>
              Spoken Communications Review
            </Typography>
            <Chip
              label={`${totalAnswered} of ${totalQuestions} answered`}
              color={overallProgress === 100 ? "success" : "default"}
              sx={{
                fontWeight: 600,
                backgroundColor:
                  overallProgress === 100
                    ? theme.palette.success.light
                    : theme.palette.action.hover,
                color:
                  overallProgress === 100
                    ? theme.palette.success.dark
                    : theme.palette.text.secondary,
              }}
            />
          </Stack>

          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{
              height: 8,
              borderRadius: 999,
              "& .MuiLinearProgress-bar": {
                borderRadius: 999,
              },
            }}
          />

          <Stepper
            alternativeLabel
            nonLinear
            activeStep={activeStep}
            sx={{
              pt: 1,
              "& .MuiStepConnector-line": {
                borderTopWidth: 2,
              },
            }}
          >
            {FORM_SECTIONS.map((section, index) => {
              const completed = getSectionProgress(section.key) === 100;
              return (
                <Step key={section.key} completed={completed}>
                  <StepLabel
                    StepIconProps={{
                      color: completed ? "primary" : "inherit",
                    }}
                    onClick={() => handleStepSelect(index)}
                    sx={{
                      cursor: "pointer",
                      "& .MuiStepLabel-label": {
                        fontSize: "0.85rem",
                        fontWeight: activeStep === index ? 700 : 500,
                        textTransform: "capitalize",
                      },
                    }}
                  >
                    {section.name.toLowerCase()}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Stack>
      </Paper>

      <Fade in key={currentSection.key} timeout={300}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: { xs: 2, md: 3 },
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Stack spacing={3}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1.5}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {currentSection.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {answeredInSection} of {currentSection.questions.length} answered
                </Typography>
              </Box>
              <Chip
                label={`${Math.round(currentSectionProgress)}% complete`}
                sx={{
                  fontWeight: 600,
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.text.secondary,
                }}
              />
            </Stack>

            {currentSection.questions.map((question) => {
              const response =
                answers?.[currentSection.key]?.[question.id] ?? {
                  answer: "",
                  comment: "",
                };
              const visuals = getAnswerVisuals(response.answer);

              return (
                <Paper
                  key={question.id}
                  variant="outlined"
                  sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    borderColor: response.answer
                      ? visuals.color
                      : theme.palette.divider,
                    backgroundColor: theme.palette.background.default,
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      borderColor: visuals.color,
                      boxShadow: "0px 12px 24px rgba(15, 23, 42, 0.08)",
                    },
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    spacing={1.5}
                  >
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{ lineHeight: 1.6 }}
                    >
                      {question.text}
                    </Typography>
                    <Chip
                      label={visuals.label}
                      sx={{
                        fontWeight: 600,
                        backgroundColor: visuals.background,
                        color: visuals.color,
                      }}
                    />
                  </Stack>

                  <ToggleButtonGroup
                    value={response.answer}
                    exclusive
                    onChange={(_, value) => {
                      if (value !== null) {
                        handleAnswerChange(
                          currentSection.key,
                          question.id,
                          value
                        );
                      }
                    }}
                    sx={{
                      width: "100%",
                      display: "grid",
                      gridTemplateColumns: isSmallScreen
                        ? "repeat(3, minmax(80px, 1fr))"
                        : "repeat(3, minmax(95px, 1fr))",
                      gap: 0.75,
                      padding: 0.35,
                      borderRadius: 999,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    {ANSWER_OPTIONS.map((option) => {
                      const isSelected = response.answer === option.value;
                      const palette = getOptionPalette(option.value);

                      return (
                        <ToggleButton
                          key={option.value}
                          value={option.value}
                          sx={{
                            borderRadius: 999,
                            borderWidth: 1,
                            borderStyle: "solid",
                            borderColor: "transparent",
                            backgroundColor: isSelected
                              ? palette.base
                              : "transparent",
                            color: isSelected
                              ? palette.contrast
                              : theme.palette.text.secondary,
                            fontWeight: 600,
                            textTransform: "none",
                            fontSize: "0.8rem",
                            letterSpacing: 0.2,
                            py: 0.75,
                            px: 1.5,
                            transition: "all 0.2s ease",
                            boxShadow: isSelected
                              ? `0 6px 16px ${palette.base}33`
                              : "none",
                            "&:hover": {
                              backgroundColor: isSelected
                                ? palette.base
                                : theme.palette.action.hover,
                              color: isSelected
                                ? palette.contrast
                                : theme.palette.text.primary,
                              boxShadow: isSelected
                                ? `0 6px 16px ${palette.base}55`
                                : "none",
                            },
                            "&.Mui-selected": {
                              backgroundColor: palette.base,
                              color: theme.palette.common.white,
                              borderColor: palette.base,
                              "&:hover": {
                                backgroundColor: palette.base,
                              },
                            },
                          }}
                        >
                          {option.label}
                        </ToggleButton>
                      );
                    })}
                  </ToggleButtonGroup>

                  <TextField
                    label="Comment (optional)"
                    placeholder="Capture any relevant observations here..."
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={response.comment}
                    onChange={(event) =>
                      handleCommentChange(
                        currentSection.key,
                        question.id,
                        event.target.value
                      )
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Paper>
              );
            })}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              spacing={1.5}
            >
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0}
                sx={{ minWidth: 140 }}
              >
                Previous section
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={activeStep === FORM_SECTIONS.length - 1}
                sx={{ minWidth: 160 }}
              >
                {activeStep === FORM_SECTIONS.length - 1
                  ? "Last section"
                  : "Next section"}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Fade>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Stack spacing={2.5}>
          <Typography variant="h6" fontWeight={600}>
            Overall assessment
          </Typography>
          <FormControl fullWidth>
            <FormLabel
              sx={{
                mb: 1,
                fontWeight: 600,
                color: theme.palette.text.secondary,
              }}
            >
              Overall rating
            </FormLabel>
            <Select
              value={overallRating}
              onChange={(event) => setOverallRating(event.target.value)}
              displayEmpty
              sx={{
                borderRadius: 2,
              }}
            >
              <MenuItem value="">
                <em>Select overall rating</em>
              </MenuItem>
              {OVERALL_RATING_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Development plan
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Optional: outline any coaching focus and next steps
            </Typography>
          </Box>
          <TextField
            label="Nature of need"
            placeholder="Describe the specific development focus..."
            multiline
            minRows={3}
            value={developmentPlan.nature_of_need}
            onChange={(event) =>
              handleDevelopmentPlanChange(
                "nature_of_need",
                event.target.value
              )
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
          <TextField
            label="Development plan"
            placeholder="Outline the actions or resources required..."
            multiline
            minRows={3}
            value={developmentPlan.plan}
            onChange={(event) =>
              handleDevelopmentPlanChange("plan", event.target.value)
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
          <TextField
            label="Target completion date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={developmentPlan.target_completion_date}
            onChange={(event) =>
              handleDevelopmentPlanChange(
                "target_completion_date",
                event.target.value
              )
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default NRSpokenCommsReviewForm;

